import { useEffect, useMemo, useRef } from 'react'
import type { Color } from '../../types/game'
import { assetUrl } from '../../utils/assets'

const PLAYER_COLORS: Color[] = ['orange', 'red', 'yellow', 'white']
// SVG points up by default; movement angle 0 = right, so add π/2 to face forward
const ROTATION_OFFSET = Math.PI / 2
const SPEED = 1.4
const MAX_TURN = 0.04
const TURN_DRIFT = 0.008
const TURN_DAMP = 0.995

interface BeeState {
  color: Color
  x: number
  y: number
  angle: number
  dAngle: number
}

function edgeSpawn(w: number, h: number): Pick<BeeState, 'x' | 'y' | 'angle'> {
  const spread = Math.PI * 0.45
  const edge = Math.floor(Math.random() * 4)
  switch (edge) {
    case 0: // top — enter moving downward
      return { x: Math.random() * w, y: -32, angle: Math.PI * 0.5 + (Math.random() - 0.5) * spread }
    case 1: // right — enter moving leftward
      return { x: w + 32, y: Math.random() * h, angle: Math.PI + (Math.random() - 0.5) * spread }
    case 2: // bottom — enter moving upward
      return { x: Math.random() * w, y: h + 32, angle: -Math.PI * 0.5 + (Math.random() - 0.5) * spread }
    default: // left — enter moving rightward
      return { x: -32, y: Math.random() * h, angle: (Math.random() - 0.5) * spread }
  }
}

function makeBee(index: number, w: number, h: number): BeeState {
  return { color: PLAYER_COLORS[index % PLAYER_COLORS.length], dAngle: 0, ...edgeSpawn(w, h) }
}

interface WanderingBeesProps {
  count?: number
}

export function WanderingBees({ count = 10 }: WanderingBeesProps) {
  const outerRefs = useRef<(HTMLDivElement | null)[]>([])
  const innerRefs = useRef<(HTMLDivElement | null)[]>([])
  const stateRef = useRef<BeeState[]>([])
  const rafRef = useRef<number>(0)
  const workerUrl = useMemo(() => assetUrl('images', 'resources', 'worker.svg'), [])

  useEffect(() => {
    const w = window.innerWidth
    const h = window.innerHeight
    stateRef.current = Array.from({ length: count }, (_, i) => makeBee(i, w, h))

    stateRef.current.forEach((bee, i) => {
      const outer = outerRefs.current[i]
      const inner = innerRefs.current[i]
      if (outer) outer.style.transform = `translate(${bee.x}px, ${bee.y}px)`
      if (inner) inner.style.transform = `translate(-50%, -50%) rotate(${bee.angle + ROTATION_OFFSET}rad)`
    })

    const tick = () => {
      const w = window.innerWidth
      const h = window.innerHeight

      stateRef.current = stateRef.current.map((bee, i) => {
        let dAngle = bee.dAngle * TURN_DAMP + (Math.random() - 0.5) * TURN_DRIFT
        if (dAngle > MAX_TURN) dAngle = MAX_TURN
        if (dAngle < -MAX_TURN) dAngle = -MAX_TURN

        const angle = bee.angle + dAngle
        const x = bee.x + Math.cos(angle) * SPEED
        const y = bee.y + Math.sin(angle) * SPEED

        const outer = outerRefs.current[i]
        const inner = innerRefs.current[i]

        if (x < -60 || x > w + 60 || y < -60 || y > h + 60) {
          const spawned = edgeSpawn(w, h)
          if (outer) outer.style.transform = `translate(${spawned.x}px, ${spawned.y}px)`
          if (inner) inner.style.transform = `translate(-50%, -50%) rotate(${spawned.angle + ROTATION_OFFSET}rad)`
          return { ...bee, ...spawned, dAngle: 0 }
        }

        if (outer) outer.style.transform = `translate(${x}px, ${y}px)`
        if (inner) inner.style.transform = `translate(-50%, -50%) rotate(${angle + ROTATION_OFFSET}rad)`
        return { ...bee, x, y, angle, dAngle }
      })

      rafRef.current = requestAnimationFrame(tick)
    }

    rafRef.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafRef.current)
  }, [count])

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        pointerEvents: 'none',
        overflow: 'hidden',
        zIndex: 1001,
      }}
    >
      {Array.from({ length: count }, (_, i) => {
        const color = PLAYER_COLORS[i % PLAYER_COLORS.length]
        return (
          <div
            key={i}
            ref={(el) => { outerRefs.current[i] = el }}
            style={{ position: 'absolute', left: 0, top: 0 }}
          >
            <div ref={(el) => { innerRefs.current[i] = el }}>
              <div
                style={{
                  width: '8em',
                  height: '8em',
                  backgroundColor: `var(--${color})`,
                  maskImage: `url(${workerUrl})`,
                  maskSize: 'contain',
                  maskRepeat: 'no-repeat',
                  maskPosition: 'center',
                  WebkitMaskImage: `url(${workerUrl})`,
                  WebkitMaskSize: 'contain',
                  WebkitMaskRepeat: 'no-repeat',
                  WebkitMaskPosition: 'center',
                } as React.CSSProperties}
              />
            </div>
          </div>
        )
      })}
    </div>
  )
}
